import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ModalBase from '../Modal/ModalBase.jsx';
import { Search } from '../icons/index.jsx';
import { searchGuides } from '../../services/guides.js';
import { emitEvent } from '../../api/events.js';

import {
  GUIDE_FILTERS_INITIAL_STATE,
  buildGuideFilters,
  GUIDE_FILTER_FIELDS,
  GUIDE_FILTER_LABELS,
} from '@/modules/guias/guidesFilters.js';
import './search.css';

export default function SearchGuidesModal({ isOpen, onClose }) {
  const titleId = useId();
  const initialFocusRef = useRef(null);
  const advancedId = useId();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [formState, setFormState] = useState(GUIDE_FILTERS_INITIAL_STATE);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const filtersPreview = useMemo(() => buildGuideFilters(formState), [formState]);
  const renderField = key => {
    const field = GUIDE_FILTER_FIELDS[key];
    const props = {
      id: `${titleId}-${key}`, name: field.name,
      className: 'search-modal__input', value: formState[key] ?? '',
      onChange: handleChange, ref: key === 'guideNumber' ? initialFocusRef : undefined,
    };
    return <div className="search-modal__group" key={key}>
      <label className="search-modal__label" htmlFor={props.id}>{field.label}</label>
      {field.type === 'select' ? <select {...props}>
        {field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select> : <input {...props} type={field.type} placeholder={field.placeholder} />}
    </div>;
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Keep ModalBase's opener capture stable across filter and parent updates.
  const handleClose = useCallback(() => {
    onCloseRef.current();
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    const filters = buildGuideFilters(formState);
    emitEvent('guides.search', { filters });
    setSearchError(false);
    setIsSearching(true);
    setHasSearched(true);
    try {
      const data = await searchGuides(filters);
      setResults(data);
    } catch {
      setResults([]);
      setSearchError(true);
    } finally {
      setIsSearching(false);
    }
  };

  const summaryText = hasSearched
    ? `${results.length} ${results.length === 1 ? 'resultado' : 'resultados'}`
    : 'Sin resultados todavía';

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy={titleId}
      initialFocusRef={initialFocusRef}
      closeOnBackdropClick={true}
      closeOnEsc={true}
      ariaLabel="Buscar Guías"
      backdropClassName="modal-backdrop modal-backdrop--search"
      viewportClassName="modal-viewport search-modal__viewport"
      className="modal modal-content search-modal__container search-modal--guides"
    >
      <div className="search-modal__header">
        <div>

          <h2 id={titleId} className="search-modal__title">
            Buscar Guías
          </h2>
          <p className="search-modal__description">Buscá por guía, remito o cliente. Combiná filtros para precisar la búsqueda.</p>

        </div>
        <button type="button" className="search-modal__close" onClick={handleClose}>
          Cerrar
        </button>
      </div>
      <form className="search-modal__form" onSubmit={handleSubmit}>
        <div className="search-modal__grid search-modal__grid--three">
          {['guideNumber', 'remitoNumber', 'party'].map(renderField)}
        </div>
        <div className="search-modal__toolbar">
        <button type="button" className="search-modal__secondary search-modal__advanced-toggle"
          aria-expanded={advancedOpen} aria-controls={advancedId}
          onClick={() => setAdvancedOpen(value => !value)}>Filtros avanzados</button>
        <div className="search-modal__actions">
          <button
            type="submit"
            className="search-modal__primary"
            disabled={isSearching}
            aria-label="Ejecutar búsqueda de guías"
          >
            <Search size={18} />
            <span>Buscar</span>

          </button>
        </div>
        </div>
        <div id={advancedId} hidden={!advancedOpen}>
          {advancedOpen && [
            ['Fechas', ['fromDate', 'toDate']],
            ['Estados y pago', ['estadoLogistico', 'estadoComercial', 'paymentCondition', 'invoiceStatus', 'collectionStatus']],
            ['Referencias', ['line', 'branchId', 'foja', 'reparto']],
            ['Intervinientes y entrega', ['sender', 'receiver', 'address']],
          ].map(([heading, keys]) => <fieldset className="search-modal__fieldset" key={heading}>
            <legend>{heading}</legend>
            <div className="search-modal__grid search-modal__grid--two">{keys.map(renderField)}</div>
          </fieldset>)}
        </div>
        {Object.keys(filtersPreview).length > 0 && <section className="search-modal__chips" aria-label="Filtros activos">
          {Object.entries(filtersPreview).map(([key, value]) => <button type="button"
            className="search-modal__secondary" key={key} aria-label={`Quitar filtro ${GUIDE_FILTER_LABELS[key]}`}
            onClick={() => setFormState(prev => ({ ...prev, [key]: '' }))}>
            {GUIDE_FILTER_LABELS[key]}: {GUIDE_FILTER_FIELDS[key]?.options?.find(option => option.value === value)?.label || value} ×
          </button>)}
          <button type="button" className="search-modal__secondary" onClick={() => setFormState(GUIDE_FILTERS_INITIAL_STATE)}>Limpiar filtros</button>
        </section>}

      </form>
      <div className="search-modal__results">
        {isSearching ? <p role="status">Buscando guías...</p> : searchError ? (
          <p role="alert">No pudimos buscar las guías. Intentá nuevamente.</p>
        ) : <>
        {hasSearched && <p className="search-modal__summary">{summaryText}</p>}
        {results.length ? (
          <>
          <p className="search-modal__helper">Vista previa de hasta 5 guías. Ajustá los filtros si necesitás otra coincidencia.</p>
          <ul className="search-modal__list" aria-label="Guías encontradas">
            {results.map(item => (
              <li key={`${item.guia}-${item.remito}`}>
                <div className="search-modal__guide-identity">
                  <h3>Guía <span>{item.guia}</span></h3>
                  <span>Remito {item.remito}</span>
                </div>
                <dl className="search-modal__guide-parties">
                  <div><dt>Remitente</dt><dd>{item.envia}</dd></div>
                  <div><dt>Destinatario</dt><dd>{item.recibe}</dd></div>
                </dl>
              </li>
            ))}
          </ul>
          </>
        ) : (
          <p className="search-modal__empty">
            {hasSearched
              ? 'No encontramos guías con los filtros actuales.'
              : 'Ajustá los filtros y presioná "Buscar" para ver coincidencias.'}
          </p>
        )}
        </>}
      </div>

    </ModalBase>
  );
}
